import React, { useEffect, useState, memo } from 'react';
import { connect } from 'react-redux';

import { buildSortByParam } from 'src/helpers/paramBuilder';
import { openInternalLink } from 'src/utils/url';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { TradeShow } from '../../components/Company/TradeShow';
import {
  loadTradeShow,
  loadTradeShowCompanies,
  loadTradeShowStatistics,
  removeEvent,
  removeUser,
  sendTradeShow,
  updateTextField,
  updateTradeShowIndustries,
  handleCompaniesTableSorting,
} from '../../actions/tradeShow';
import { isDeepChanged, toJS, unwrap } from '../../utils/ChangeSpy';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES } from '../../utils/industryTagsHelper';
import { showError, showInformation } from '../../utils/MessagePopup';
import { createTradeShowErrorMessages } from '../../utils/createTradeShowErrorMessages';
import { usePopupContext } from '../../contexts/PopupContext';

const ALL_FILTER = 'All';
const EVENT_DELETE_INFO =
  'Are you sure you want to delete this Trade Show Event? This is irreversible, and all target companies to which this event was assigned will lose the associate with this date.';

const TradeShowContainer = ({
  loadTradeShow,
  loadTradeShowStatistics,
  loadTradeShowCompanies,
  updateTextField,
  removeEvent,
  removeUser,
  updateTradeShowIndustries,
  sendTradeShow,
  handleCompaniesTableSorting,
  match,
  history,
  sortModel,
  tradeShow,
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const [tradeShowErrors, setTradeShowErrors] = useState({
    industryCategoriesError: false,
    businessModelsError: false,
    usersError: false,
    nameError: false,
    abbrError: false,
    descriptionError: false,
  });
  const { tradeShowId } = match.params;

  const [yearFilter, setYearFilter] = useState(ALL_FILTER);

  useEffect(() => {
    if (tradeShowId) {
      loadTradeShow(tradeShowId);
    }
  }, [loadTradeShow, tradeShowId]);

  useEffect(() => {
    if (tradeShowId) {
      loadTradeShowStatistics(tradeShowId, yearFilter);
    }
  }, [loadTradeShowStatistics, yearFilter, tradeShowId]);

  useEffect(() => {
    if (tradeShowId) {
      const sortBy = buildSortByParam(tradeShow);

      loadTradeShowCompanies({
        tradeShowId,
        year: yearFilter,
        sortBy: sortBy === '' ? null : sortBy,
      });
    }
  }, [tradeShowId, sortModel, yearFilter]);

  useEffect(() => {
    if (
      tradeShowErrors.abbrError ||
      tradeShowErrors.businessModelsError ||
      tradeShowErrors.descriptionError ||
      tradeShowErrors.nameError ||
      tradeShowErrors.usersError ||
      tradeShowErrors.industryCategoriesError
    ) {
      showError(openPopup, createTradeShowErrorMessages(tradeShowErrors));
    }
  }, [tradeShowErrors, openPopup]);

  const dataValidation = () => {
    const tradeShowObject = toJS(tradeShow);

    setTradeShowErrors({
      industryCategoriesError: !tradeShowObject.industryCategories.length,
      businessModelsError: !tradeShowObject.businessModels.length,
      usersError: !tradeShowObject.users.length,
      nameError: !tradeShowObject.name.value,
      abbrError: !tradeShowObject.abbr.value,
      descriptionError: !tradeShowObject.description.value,
    });

    return !(
      !tradeShowObject.industryCategories.length ||
      !tradeShowObject.businessModels.length ||
      !tradeShowObject.users.length ||
      !tradeShowObject.name.value ||
      !tradeShowObject.abbr.value ||
      !tradeShowObject.description.value
    );
  };

  const handleTextChange = (event, name) => {
    const { value } = event.target;

    updateTextField(name, value);
  };

  const handleEventRowInsert = () => {
    openPopup('AddTradeShowEventPopup', {});
  };

  const handleEventRemove = id => {
    openPopup('ConfirmPopup', {
      message: EVENT_DELETE_INFO,
      onOk: () => {
        removeEvent(id);
        closePopup();
      },
      onCancel: () => closePopup(),
      yes: 'Delete',
      no: 'Cancel',
    });
  };

  const handleIndustryClick = () => {
    const tags = unwrap(tradeShow.get(INDUSTRY_CATEGORIES));
    const models = unwrap(tradeShow.get(BUSINESS_MODELS));

    openPopup('TagsManagementPopup', {
      addNew: false,
      clientView: true,
      selectedTags: tags.reduce((accumulator, t) => {
        accumulator[t.get('id')] = true;

        return accumulator;
      }, {}),
      selectedModels: models.reduce((accumulator, m) => {
        accumulator[m.get('id')] = true;

        return accumulator;
      }, {}),
      callback: (tagsSelected, modelsSelected) => {
        updateTradeShowIndustries(tagsSelected, modelsSelected);
      },
    });
  };

  const handleRowDelete = (event, { id }) => {
    removeUser(id);
  };

  const handleYearFilterChange = value => {
    setYearFilter(value);
  };

  const handleSubmit = () => {
    if (dataValidation()) {
      const afterSuccess = !tradeShowId
        ? action => {
            history.push(`/trade-show/${action.response.data.id}`);
            showInformation(openPopup, 'Trade Show was added successfully.');
          }
        : () => {
            showInformation(openPopup, 'Trade Show was saved successfully.');
          };

      sendTradeShow(tradeShowId, toJS(tradeShow), afterSuccess);
    }
  };

  if (tradeShow.get('addedAsNew')) {
    history.push(`/trade-show/${tradeShow.get('id')}`);
  }

  const tags = tradeShow.get(INDUSTRY_CATEGORIES);
  const models = tradeShow.get(BUSINESS_MODELS);

  const handleCompaniesSortingChange = data => {
    handleCompaniesTableSorting({
      filterName: data.filterName,
      filterData: data.filterData,
    });
  };

  const handleGetNextPageData = page => {
    const sortBy = buildSortByParam(tradeShow);

    loadTradeShowCompanies({
      tradeShowId,
      year: yearFilter,
      sortBy,
      page,
    });
  };

  const handleRowDoubleClick = ({ data: { targetId } }) => {
    openInternalLink(`/company/${targetId}`);
  };

  return (
    <TradeShow
      businessModels={models}
      data={tradeShow}
      disableSave={!isDeepChanged(tradeShow) || tradeShow.get('sending')}
      errors={tradeShowErrors}
      industryTags={tags}
      onAddEvent={handleEventRowInsert}
      onCompaniesSortingChange={handleCompaniesSortingChange}
      onEventRemove={handleEventRemove}
      onGetNextPageData={handleGetNextPageData}
      onIndustryClick={handleIndustryClick}
      onRowDelete={handleRowDelete}
      onRowDoubleClicked={handleRowDoubleClick}
      onSubmit={handleSubmit}
      onTextChange={handleTextChange}
      onYearFilterChange={handleYearFilterChange}
      tradeShowId={tradeShowId}
      yearFilter={yearFilter}
    />
  );
};

function mapStateToProps(state) {
  return {
    tradeShow: state.tradeShow,
    sortModel: state.tradeShow.getIn(['sortBy', 'sortModel']),
  };
}

export default connect(
  mapStateToProps,
  {
    updateTextField,
    loadTradeShow,
    loadTradeShowCompanies,
    loadTradeShowStatistics,
    updateTradeShowIndustries,
    sendTradeShow,
    removeEvent,
    removeUser,
    handleCompaniesTableSorting,
  },
  mergeProps,
  connectOptions,
)(memo(TradeShowContainer));
